import React, { useState, useEffect, useRef } from "react";
import {
  Building2,
  Users,
  MapPin,
  Camera,
  Radio,
  ShieldCheck,
  Code2,
  Smartphone,
  Network,
  ClipboardList,
  ArrowRight,
  Mail,
  Phone,
  Award,
  Globe,
  Landmark,
  ScanLine,
  Menu,
  X,
  CheckCircle2,
  ChevronDown,
  Briefcase,
  TrendingUp,
  Clock,
  Target,
  Layers,
  Sparkles,
  Rocket,
  Calendar,
} from "lucide-react";

// ========== IMPORT ViewRequirement COMPONENT ==========
import ViewRequirement from '../pages/ViewRequirement/ViewRequirement';
import NoticeBoard from './notice';
// ========== IMAGE PATHS - UPDATE WITH YOUR IMAGE LOCATIONS ==========
const IMAGES = {
  logo: require('./jmk.png'),
  heroImage: require('./images/hero-main.jpg'),
  aboutImage: require('./images/about-company.png'),
  capabilitiesImage: require('./images/capabilities.png'),
  servicesImage: require('./images/services-overview.png'),
  reachImage: require('./images/maharashtra-map.png'),
  projectsImage: require('./images/projects-showcase.png'),
  clientsImage: require('./images/clients-logos.png'),
};

// ========== COMPANY EMAIL ==========
const COMPANY_EMAIL = "contact@jmkinfosoft.com";

// ========== SECTION DATA ==========
const SECTIONS = [
  { id: "top", label: "Home" },
  { id: "about", label: "About" },
  { id: "capabilities", label: "Capabilities" },
  { id: "services", label: "Services" },
  { id: "reach", label: "Reach" },
  { id: "projects", label: "Projects" },
  { id: "clients", label: "Clients" },
  { id: "careers", label: "Careers" },
  { id: "contact", label: "Contact" },
];

const DIRECTORS = ["Mr. Mukund Mangulkar", "Mr. Kishor Lahurikar", "Mr. Bhagwan Raut"];

// ========== DETAILED DIVISION DATA ==========
const DIVISIONS = [
  {
    id: "aur",
    name: "Chh. Sambhaji Nagar (Aurangabad) Division",
    shortName: "Aurangabad",
    since: "2002",
    talukas: "9 Talukas",
    detail: "Flagship Setu Suvidha Kendra since 2002 — 9 talukas. Election videography since 2004. Ration card & NPR digitization centres.",
    projects: [
      "Setu Suvidha Kendra — 2002 till date",
      "Election Videography — 2004 onwards",
      "Ration Card Digitization",
      "NPR Digitization Centres"
    ],
    stats: {
      talukas: "9",
      yearsActive: "24+",
      recordsProcessed: "50 Lakh+"
    }
  },
  {
    id: "pun",
    name: "Pune Division",
    shortName: "Pune",
    since: "2016",
    talukas: "15 + 7 Talukas",
    detail: "Setu Suvidha Kendra across 15 + 7 talukas. 60 PMC ward offices staffed for tax collection (2022–25). NPR digitization, 60 lakh+ records.",
    projects: [
      "Setu Suvidha Kendra — 2016-2022 & 2023 till date",
      "PMC Tax Collection — 60 Ward Offices",
      "NPR Digitization — 60 Lakh+ Records"
    ],
    stats: {
      talukas: "22",
      yearsActive: "8+",
      recordsProcessed: "60 Lakh+"
    }
  },
  {
    id: "kon",
    name: "Konkan Division (Thane / Mumbai Suburban)",
    shortName: "Konkan",
    since: "2014",
    talukas: "3 Talukas",
    detail: "Setu Suvidha Kendra, Thane — 3 talukas. NPR digitization, 65 lakh+ records. Election CCTV & videography, Thane and Mumbai Suburban.",
    projects: [
      "Setu Suvidha Kendra — 2014-2019 & 2023 till date",
      "NPR Digitization — 65 Lakh+ Records",
      "Election CCTV & Videography"
    ],
    stats: {
      talukas: "3",
      yearsActive: "10+",
      recordsProcessed: "65 Lakh+"
    }
  },
  {
    id: "nsk",
    name: "Nashik Division",
    shortName: "Nashik",
    since: "2016",
    talukas: "15 Talukas",
    detail: "NPR booklet digitization, 55 lakh+ records across Nashik Municipal Corporation and 15 taluka offices. Statewide land-records staffing.",
    projects: [
      "NPR Booklet Digitization — 55 Lakh+ Records",
      "Nashik Municipal Corporation — 4 Ward Offices",
      "Statewide Land-Records Staffing"
    ],
    stats: {
      talukas: "15",
      yearsActive: "8+",
      recordsProcessed: "55 Lakh+"
    }
  },
  {
    id: "amr",
    name: "Amravati Division",
    shortName: "Amravati",
    since: "2011",
    talukas: "3 Districts",
    detail: "SECC 2011 door-to-door enumeration across Amravati, Yavatmal and Akola. Live election webcasting, 2019.",
    projects: [
      "SECC 2011 — Door-to-Door Enumeration",
      "Live Election Webcasting — 2019",
      "3 District Coverage"
    ],
    stats: {
      talukas: "3",
      yearsActive: "13+",
      recordsProcessed: "30 Lakh+"
    }
  },
  {
    id: "ngp",
    name: "Nagpur Division",
    shortName: "Nagpur",
    since: "2011",
    talukas: "2 Districts",
    detail: "SECC enumeration across Gondia and Bhandara. Live election webcasting from Nagpur, 2014. Statewide GIS resource deployment.",
    projects: [
      "SECC Enumeration — Gondia & Bhandara",
      "Live Election Webcasting — 2014",
      "Statewide GIS Resource Deployment"
    ],
    stats: {
      talukas: "2",
      yearsActive: "13+",
      recordsProcessed: "25 Lakh+"
    }
  }
];

const CAPABILITIES = [
  "25+ years' experience in large Government projects under BOOT & PPP models",
  "Proven expertise in office modernization, digitization & IT-enabled citizen services",
  "Strong workforce of 500+ on-roll employees with statewide deployment capability",
  "End-to-end capability: Design → Implementation → Operation → Maintenance",
  "Experience in IT infrastructure, service counters & queue management systems",
  "Expertise in document digitization & records management for high-volume offices",
  "Adequate resources to manage multi-location, public-facing government operations",
  "Full compliance with Government standards, security norms & SLAs",
];

const SERVICES = [
  { icon: Landmark, label: "BOOT / PPP Model Projects Execution" },
  { icon: Network, label: "Managed Services Outsourcing" },
  { icon: Users, label: "IT Staffing and Consulting" },
  { icon: ClipboardList, label: "Talent Acquisition and Technical Recruitment" },
  { icon: ShieldCheck, label: "CCTV Surveillance" },
  { icon: Radio, label: "Live Webcasting / Streaming" },
  { icon: Camera, label: "Videography During Election Code of Conduct" },
  { icon: Building2, label: "IT Infrastructure and Networking" },
  { icon: MapPin, label: "Field Survey Execution" },
  { icon: ScanLine, label: "Document Management Services" },
  { icon: Code2, label: "Software / Web Development" },
  { icon: Smartphone, label: "Mobile Applications Development" },
];

const PROJECT_TABS = [
  {
    id: "boot",
    label: "BOOT & Citizen Centres",
    intro: "Design, build and run Setu Suvidha Kendras — citizen facility centres inside District Collector offices — under long-term BOOT contracts.",
    cards: [
      {
        title: "Setu Suvidha Kendra — Chh. Sambhaji Nagar",
        meta: "2002 – till date · 9 talukas",
        body: "IT infrastructure, networking, furniture and electrical fit-out, plus full operations and maintenance for the full contract period.",
      },
      {
        title: "Setu Suvidha Kendra — Pune",
        meta: "2016–2022 & 2023 – till date · 15 + 7 talukas",
        body: "Two consecutive contract cycles running citizen-facility infrastructure across Pune district.",
      },
      {
        title: "Setu Suvidha Kendra — Thane",
        meta: "2014–2019 & 2023 – till date · 3 talukas",
        body: "Repeat-awarded BOOT contract for citizen services infrastructure in Konkan division.",
      },
      {
        title: "Setu Suvidha Kendra — Latur",
        meta: "2016 – till date · 11 talukas",
        body: "Ongoing citizen-facility centre operations across Latur district.",
      },
      {
        title: "CFC Manpower — Pune Municipal Corporation",
        meta: "2022 – 2025 · 60 ward offices",
        body: "150 office executives deployed across PMC ward offices for property-tax collection support.",
      },
    ],
  },
  {
    id: "staffing",
    label: "IT Staffing to Govt.",
    intro: "Manpower, IT staffing, consulting and technical recruitment supplied directly into Maharashtra Government departments for over a decade.",
    cards: [
      {
        title: "Land Records Department — Statewide GIS Staffing",
        meta: "400+ resources · all 358 talukas",
        body: "Recruitment and deployment of GIS (Geo-Informatics System) professionals for the Settlement Commissioner & Director of Land Records, Maharashtra, and Dy. DLR offices of every division.",
      },
      {
        title: "Departments Served",
        meta: "10+ years, ongoing",
        body: "Mantralaya Mumbai · MNREGA/MREGS (Thane, Pune, Aurangabad) · Mhada offices · Office of the Registrar, Aurangabad · District Planning Offices (Mumbai, Thane, Aurangabad) · Divisional Commissioner Office, Aurangabad · District Collector offices (Thane, Pune, Aurangabad) · Bombay High Court, Aurangabad Bench.",
      },
    ],
  },
  {
    id: "elections",
    label: "Election Projects",
    intro: "Technical and field support during the Model Code of Conduct for Lok Sabha and Vidhan Sabha elections, since 1999.",
    cards: [
      {
        title: "Videography Services",
        meta: "Loksabha & Vidhansabha",
        body: "Aurangabad (2004, 2009, 2014, 2019, 2024) · Jalna (2009, 2014, 2019) · Pune (2024) · Mumbai Suburban (2014, 2019, 2024) · Thane (2014, 2019, 2024).",
      },
      {
        title: "Live Webcasting — Poll Day",
        meta: "Loksabha & Vidhansabha",
        body: "Nagpur & Parbhani (2014) · Pune, Aurangabad, Jalna, Parbhani, Buldhana, Mumbai Suburban, Amravati (2019) · Pune & Mumbai Suburban (2024).",
      },
      {
        title: "CCTV Surveillance",
        meta: "Loksabha & Vidhansabha",
        body: "Thane & Mumbai Suburban (2019, 2024) · Pune (2024).",
      },
    ],
  },
  {
    id: "psu",
    label: "Central Govt. & PSU",
    intro: "Large-scale enumeration, biometric capture and data-digitization programmes executed on behalf of BEL, ECIL, ITI Limited and state governments.",
    cards: [
      {
        title: "Socio Economic & Caste Census (SECC) 2011",
        meta: "2.25 Crore records · Bharat Electronics Ltd.",
        body: "India's first paperless census, conducted door-to-door on hand-held Android devices. 10,000-operator team covering Mumbai Suburban, Aurangabad, Beed, Nanded, Parbhani, Dhule, Jalgaon, Latur, Amravati, Yavatmal, Akola, Gondia, Nandurbar and Thane.",
      },
      {
        title: "SECC — New Members Data Digitization",
        meta: "2014 · 1.43 Crore records across 3 states",
        body: "Maharashtra: 80 lakh (BEL) · Andhra Pradesh: 60 lakh (ECIL) · Goa: 3 lakh (ITI Limited).",
      },
      {
        title: "NPR — Unmerged Population Data Entry",
        meta: "2016 · Bharat Electronics Ltd.",
        body: "Maharashtra 80.35 lakh · Gujarat 39.53 lakh · Haryana & Chhattisgarh 23 lakh · Goa 1.61 lakh.",
      },
      {
        title: "UID/EID Capture for Army Cantonment Boards",
        meta: "≈1 lakh records · Maharashtra & Tamil Nadu",
        body: "Biometric and demographic data capture of army personnel, with field teams visiting every cantonment board across both states.",
      },
      {
        title: "Socio Educational Survey — Govt. of Karnataka",
        meta: "2015 · 21 lakh records",
        body: "11 digitization centres, 400 trained resources. Bilingual five-page, 80-field forms cross-validated against Aadhaar, EPIC, SSA and ration-card databases.",
      },
      {
        title: "Aadhaar Enrollments",
        meta: "2011 · 5 lakh enrollments",
        body: "150 Aadhaar kits deployed across Pune and Aurangabad cities.",
      },
      {
        title: "MFID Capture — Coastal Fisheries, Goa",
        meta: "2 lakh records",
        body: "Biometric and demographic camps run across every coastal location in Goa for the fisheries community.",
      },
    ],
  },
  {
    id: "state",
    label: "Maharashtra State Govt.",
    intro: "Statewide digitization programmes commissioned directly by district administrations across Maharashtra.",
    cards: [
      {
        title: "National Population Register — Booklet Digitization",
        meta: "2016–17 · 1.80 Crore records · ~100 centres",
        body: "Pune 60 lakh+ (24 tehsils + 4 PMC zonal offices) · Thane 65 lakh+ (19 municipal & taluka offices) · Nashik 55 lakh+ (16 offices).",
      },
      {
        title: "Ration Card Online Digitization",
        meta: "2012–13 · 40 lakh cards",
        body: "Bilingual data entry on the ERCMS portal for Mumbai, Thane and Aurangabad. 300 computers, 500 operators across three shifts.",
      },
      {
        title: "NPR Data Digitization from Scanned Images",
        meta: "2011–12 · 1.86 Crore records · completed in 11 months",
        body: "Purpose-built 250-computer data centre, 500 operators across three shifts, covering Akola, Buldhana, Amravati, Washim, Jalna, Nanded, Parbhani, Yavatmal, Hingoli and Latur.",
      },
    ],
  },
  {
    id: "wipro",
    label: "WIPRO Partnership",
    intro: "Delivery partner to WIPRO Limited on two of India's largest field-digitization and maintenance programmes.",
    cards: [
      {
        title: "CCTNS — Crime & Criminal Tracking Network",
        meta: "2016–17 · FIR digitization · 40 lakh forms · Maharashtra Police / NCRB",
        body: "40 digitization centres inside SP and CP offices across 29 districts, including Mumbai, Thane, Pune, Nashik, Nagpur and Nanded.",
      },
      {
        title: "eMMS — Electronic Maintenance Management System",
        meta: "2017–2022 · Indian Air Force",
        body: "Rolled out across roughly 40 IAF stations spanning Rajasthan, Haryana, J&K, Assam, Bihar, Delhi, MP, Punjab, West Bengal, Tamil Nadu, Telangana, Gujarat, Karnataka, Maharashtra, UP and the Andaman & Nicobar Islands.",
      },
    ],
  },
  {
    id: "software",
    label: "Software & Web",
    intro: "Two decades of custom e-governance software — several built years ahead of the state's own digitization curve.",
    cards: [
      {
        title: "Entertainment Tax Collection — Cable TV Operators",
        meta: "2003–2005 · Collector Office, Aurangabad",
        body: "Cross-referenced MSEB consumer records to establish the true count of cable subscribers per operator.",
      },
      {
        title: "Non-Agriculture (NA) eChallan & Tax Collection",
        meta: "2004–2005 · 1,362 villages",
        body: "Server-client software used by Circle Officers and Talathis to generate NA-tax e-challans.",
      },
      {
        title: "PRAMAAN — NREGA / MREGS Software",
        meta: "Collector Office, Jalna",
        body: "Web-based accountability and task-tracking system. The implementation earned Collector Ranga Naik (IAS) recognition from the President of India.",
      },
      {
        title: "SAHARA Bonds Digitization for SEBI",
        meta: "via SHCIL · 1 crore stakeholder records",
        body: "Full data capture and processing of Sahara bondholder records in a custom application, delivered within one year.",
      },
      {
        title: "GIS Water-Tanker Tracking",
        meta: "Zilla Parishad, Jalna",
        body: "Web application paired with an Android app to pin exact tanker locations, capture spot-site photos and generate MIS reports.",
      },
      {
        title: "Web Development Practice",
        meta: "Static · Dynamic · Business + Blog",
        body: "Custom sites and database-backed applications on Java, ASP.NET, PHP/MySQL, C#, Oracle, SQL Server, WordPress, Joomla and Drupal.",
      },
    ],
  },
];

const CLIENTS = [
  "Government of Maharashtra",
  "Election Commission of India",
  "Census of India",
  "Bharat Electronics Ltd.",
  "ECIL",
  "ITI Limited",
  "WIPRO Limited",
  "Indian Air Force",
  "SEBI / SHCIL",
  "Government of Karnataka",
  "UIDAI",
  "Pune Municipal Corporation",
];

// const STATS = [
//   { value: "25+", label: "Years Experience", icon: Clock },
//   { value: "500+", label: "On-roll Employees", icon: Users },
//   { value: "358", label: "Talukas Covered", icon: MapPin },
// ];

function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!els.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [ids]);
  return active;
}

export default function JMKInfosoftWebsite() {
  const [navOpen, setNavOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(PROJECT_TABS[0].id);
  const [activeDiv, setActiveDiv] = useState(DIVISIONS[0].id);
  const activeTabData = PROJECT_TABS.find((t) => t.id === activeTab);
  const activeDivision = DIVISIONS.find((d) => d.id === activeDiv);

  const sectionIds = SECTIONS.map((s) => s.id);
  const activeSection = useActiveSection(sectionIds);
  const activeLabel = SECTIONS.find((s) => s.id === activeSection)?.label || "Home";

  const goTo = (id) => {
    setNavOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // ========== EMAIL FUNCTION ==========
  const openEmail = (email, subject = "Enquiry from JMK Infosoft Website", body = "") => {
    // Gmail compose link उघडा
    const gmailLink = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(gmailLink, '_blank');
  };

  return (
    <div className="jmk-root">
    <style>{`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Playfair+Display:wght@600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

  :root {
    --teal: #008986;
    --teal-dark: #00524f;
    --teal-light: #eaf3f2;
    --teal-gradient: linear-gradient(135deg, #00524f 0%, #008986 50%, #00a89e 100%);
    --white: #ffffff;
    --ink: #1a2422;
    --gray: #5c6c6a;
    --gray-light: #f0f4f3;
    --border: #cddad8;
    --shadow-sm: 0 1px 3px rgba(0,0,0,0.06);
    --shadow-md: 0 4px 16px rgba(0,0,0,0.08);
    --shadow-lg: 0 8px 32px rgba(0,0,0,0.10);
    --shadow-xl: 0 12px 48px rgba(0,0,0,0.12);
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  .jmk-root {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    background: var(--white);
    color: var(--ink);
    line-height: 1.65;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  .jmk-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 40px;
  }

  /* Scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: var(--teal-light);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--teal);
    border-radius: 4px;
  }

  /* =========================================================
     UTILITY BAR
  ========================================================= */

  .jmk-utility {
    background: var(--teal-dark);
    color: rgba(255,255,255,0.9);
    font-size: 12px;
    padding: 8px 0;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    font-family: 'Inter', sans-serif;
  }

  .jmk-utility .jmk-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  .jmk-utility-links {
    display: flex;
    gap: 28px;
    align-items: center;
  }

  .jmk-utility-links span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .jmk-utility-links a {
    color: rgba(255,255,255,0.9);
    text-decoration: none;
    transition: color 0.2s;
  }

  .jmk-utility-links a:hover {
    color: #fff;
  }

  .jmk-utility-badge {
    background: rgba(255,255,255,0.10);
    padding: 3px 14px;
    border-radius: 20px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-family: 'Inter', sans-serif;
  }

  /* =========================================================
     HEADER
  ========================================================= */

  .jmk-header {
    background: rgba(255,255,255,0.98);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border);
    position: sticky;
    top: 0;
    z-index: 1000;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    transition: box-shadow 0.3s ease;
  }

  .jmk-header.scrolled {
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  }

  .jmk-header-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 40px;
    max-width: 1200px;
    margin: 0 auto;
  }

  .jmk-brand {
    display: flex;
    align-items: center;
    gap: 14px;
    cursor: pointer;
    text-decoration: none;
    flex-shrink: 0;
  }

  .jmk-logo-img {
    height: 44px;
    width: auto;
    object-fit: contain;
  }

  .jmk-brand-text {
    display: flex;
    flex-direction: column;
  }

  .jmk-brand-name {
    font-family: 'Playfair Display', serif;
    font-weight: 800;
    font-size: 18px;
    color: var(--teal-dark);
    line-height: 1.1;
    letter-spacing: -0.02em;
  }

  .jmk-brand-tag {
    font-size: 9px;
    color: var(--gray);
    text-transform: uppercase;
    letter-spacing: 0.15em;
    font-weight: 600;
    font-family: 'Inter', sans-serif;
  }

  .jmk-burger {
    display: none;
    background: none;
    border: none;
    padding: 8px;
    cursor: pointer;
    color: var(--teal-dark);
  }

  /* =========================================================
     NAVIGATION
  ========================================================= */

  .jmk-nav-wrapper {
    position: sticky;
    top: 62px;
    z-index: 999;
    background: var(--teal-dark);
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }

  .jmk-nav {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 40px;
    display: flex;
    gap: 2px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }

  .jmk-nav::-webkit-scrollbar {
    display: none;
  }

  .jmk-nav button {
    background: none;
    border: none;
    color: rgba(255,255,255,0.75);
    font-size: 12.5px;
    font-weight: 500;
    padding: 12px 18px;
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    border-bottom: 3px solid transparent;
    transition: all 0.25s ease;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .jmk-nav button:hover {
    color: #fff;
    background: rgba(255,255,255,0.06);
  }

  .jmk-nav button.active {
    color: #fff;
    border-bottom-color: #ffffff;
    font-weight: 600;
  }

  .jmk-mobilemenu {
    display: none;
    flex-direction: column;
    background: var(--teal-dark);
    padding: 8px 40px 16px;
    position: sticky;
    top: 62px;
    z-index: 998;
  }

  .jmk-mobilemenu.open {
    display: flex;
  }

  .jmk-mobilemenu button {
    text-align: left;
    background: none;
    border: none;
    color: rgba(255,255,255,0.85);
    padding: 12px 0;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    font-size: 13px;
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .jmk-mobilemenu button:hover {
    color: #fff;
  }

  /* =========================================================
     BREADCRUMB
  ========================================================= */

  .jmk-breadcrumb {
    background: var(--teal-light);
    border-bottom: 1px solid var(--border);
    font-size: 12px;
    color: var(--gray);
    padding: 8px 0;
    font-family: 'Inter', sans-serif;
  }

  .jmk-breadcrumb .jmk-container {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .jmk-breadcrumb b {
    color: var(--teal-dark);
    font-weight: 600;
  }

  /* =========================================================
     HERO
  ========================================================= */

  .jmk-hero {
    background: var(--teal-gradient);
    color: #fff;
    padding: 80px 0 60px;
    position: relative;
    overflow: hidden;
  }

  .jmk-hero::before {
    content: '';
    position: absolute;
    top: -30%;
    right: -10%;
    width: 50%;
    height: 160%;
    background: rgba(255,255,255,0.03);
    transform: rotate(20deg);
    pointer-events: none;
  }

  .jmk-hero-grid {
    display: grid;
    grid-template-columns: 1.2fr 0.8fr;
    gap: 60px;
    align-items: center;
    position: relative;
    z-index: 1;
  }

  .jmk-hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    border: 1px solid rgba(255,255,255,0.25);
    padding: 6px 16px;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    font-weight: 600;
    font-family: 'JetBrains Mono', monospace;
    margin-bottom: 24px;
  }

  .jmk-hero h1 {
    font-family: 'Playfair Display', serif;
    font-size: clamp(32px, 4.2vw, 46px);
    line-height: 1.12;
    font-weight: 800;
    letter-spacing: -0.02em;
  }

  .jmk-hero-sub {
    font-size: 16px;
    color: rgba(255,255,255,0.85);
    max-width: 520px;
    margin-top: 16px;
    line-height: 1.8;
    font-weight: 300;
    font-family: 'Inter', sans-serif;
  }

  .jmk-hero-ctas {
    display: flex;
    gap: 16px;
    margin-top: 32px;
    flex-wrap: wrap;
  }

  .jmk-btn-primary {
    background: #fff;
    color: var(--teal-dark);
    border: none;
    padding: 13px 30px;
    font-weight: 600;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    font-size: 13px;
    font-family: 'Inter', sans-serif;
    transition: all 0.25s;
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  }

  .jmk-btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(0,0,0,0.2);
  }

  .jmk-btn-ghost {
    background: transparent;
    color: #fff;
    border: 1.5px solid rgba(255,255,255,0.3);
    padding: 13px 30px;
    border-radius: 4px;
    font-weight: 500;
    cursor: pointer;
    font-size: 13px;
    font-family: 'Inter', sans-serif;
    transition: all 0.25s;
  }

  .jmk-btn-ghost:hover {
    background: rgba(255,255,255,0.08);
    border-color: rgba(255,255,255,0.6);
  }

  .jmk-snapshot {
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 8px 40px rgba(0,0,0,0.2);
  }

  .jmk-snapshot-head {
    background: var(--teal);
    color: #fff;
    font-size: 11px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 12px 22px;
    font-weight: 600;
    font-family: 'JetBrains Mono', monospace;
  }

  .jmk-snapshot-row {
    display: flex;
    justify-content: space-between;
    padding: 12px 22px;
    border-bottom: 1px solid var(--gray-light);
    font-size: 13px;
    gap: 12px;
    font-family: 'Inter', sans-serif;
  }

  .jmk-snapshot-row:last-child {
    border-bottom: none;
  }

  .jmk-snapshot-row span:first-child {
    color: var(--gray);
    font-weight: 500;
  }

  .jmk-snapshot-row span:last-child {
    font-weight: 700;
    color: var(--teal-dark);
  }

  /* =========================================================
     STATS
  ========================================================= */

  .jmk-stats-bar {
    background: var(--white);
    border-bottom: 1px solid var(--border);
    padding: 36px 0;
  }

  .jmk-stats-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 40px;
    max-width: 800px;
    margin: 0 auto;
  }

  .jmk-stat-item {
    display: flex;
    align-items: center;
    gap: 16px;
    justify-content: center;
  }

  .jmk-stat-icon {
    width: 48px;
    height: 48px;
    background: var(--teal-light);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--teal);
    flex-shrink: 0;
  }

  .jmk-stat-value {
    font-family: 'Playfair Display', serif;
    font-size: 26px;
    font-weight: 800;
    color: var(--teal-dark);
    line-height: 1.1;
  }

  .jmk-stat-label {
    font-size: 12px;
    color: var(--gray);
    font-weight: 500;
    font-family: 'Inter', sans-serif;
  }

  /* =========================================================
     SECTIONS
  ========================================================= */

  .jmk-section {
    padding: 72px 0;
    border-bottom: 1px solid var(--border);
  }

  .jmk-section:last-of-type {
    border-bottom: none;
  }

  .jmk-band-white {
    background: var(--white);
  }

  .jmk-band-light {
    background: var(--teal-light);
  }

  .jmk-sechead {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 16px;
    border-bottom: 3px solid var(--teal);
    padding-bottom: 16px;
    margin-bottom: 40px;
  }

  .jmk-secnum {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: var(--teal);
    letter-spacing: 0.15em;
    font-weight: 600;
    text-transform: uppercase;
    margin-bottom: 4px;
  }

  .jmk-h2 {
    font-family: 'Playfair Display', serif;
    font-size: clamp(26px, 3vw, 34px);
    color: var(--teal-dark);
    line-height: 1.2;
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  .jmk-lede {
    font-size: 16px;
    color: var(--gray);
    max-width: 700px;
    margin-top: 8px;
    line-height: 1.8;
    font-family: 'Inter', sans-serif;
  }

  /* =========================================================
     CONTENT ROW
  ========================================================= */

  .jmk-content-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 50px;
    align-items: center;
    margin-top: 30px;
  }

  .jmk-content-row.reverse {
    direction: rtl;
  }

  .jmk-content-row.reverse > * {
    direction: ltr;
  }

  .jmk-content-image {
    border-radius: 10px;
    overflow: hidden;
    box-shadow: var(--shadow-md);
    background: var(--teal-light);
    position: relative;
  }

  .jmk-content-image img {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.4s ease;
  }

  .jmk-content-image:hover img {
    transform: scale(1.02);
  }

  .jmk-content-text h3 {
    font-family: 'Playfair Display', serif;
    font-size: 22px;
    color: var(--teal-dark);
    font-weight: 700;
    margin-bottom: 14px;
  }

  .jmk-content-text p {
    font-size: 14px;
    color: var(--gray);
    line-height: 1.8;
    font-family: 'Inter', sans-serif;
  }

  .jmk-content-text ul {
    list-style: none;
    padding: 0;
  }

  .jmk-content-text ul li {
    padding: 7px 0;
    font-size: 13.5px;
    color: var(--gray);
    display: flex;
    align-items: flex-start;
    gap: 12px;
    line-height: 1.6;
    border-bottom: 1px solid var(--gray-light);
    font-family: 'Inter', sans-serif;
  }

  .jmk-content-text ul li:last-child {
    border-bottom: none;
  }

  .jmk-content-text ul li svg {
    color: var(--teal);
    flex-shrink: 0;
    margin-top: 2px;
  }

  .jmk-content-text ul li strong {
    color: var(--ink);
    font-weight: 600;
  }

  /* =========================================================
     CAPABILITIES
  ========================================================= */

  .jmk-cap-list {
    border: 1px solid var(--border);
    border-radius: 8px;
    overflow: hidden;
    margin-top: 30px;
  }

  .jmk-cap-item {
    display: grid;
    grid-template-columns: 44px 1fr;
    gap: 18px;
    padding: 16px 22px;
    border-bottom: 1px solid var(--border);
    align-items: baseline;
    background: #fff;
    transition: background 0.2s;
    font-family: 'Inter', sans-serif;
  }

  .jmk-cap-item:hover {
    background: var(--teal-light);
  }

  .jmk-cap-item:nth-child(even) {
    background: var(--teal-light);
  }

  .jmk-cap-item:nth-child(even):hover {
    background: #d4edeb;
  }

  .jmk-cap-item:last-child {
    border-bottom: none;
  }

  .jmk-cap-num {
    font-family: 'JetBrains Mono', monospace;
    color: var(--teal);
    font-size: 13px;
    font-weight: 700;
  }

  .jmk-cap-text {
    font-size: 14px;
    color: var(--ink);
  }

  /* =========================================================
     SERVICES
  ========================================================= */

  .jmk-services-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-top: 30px;
  }

  .jmk-service-card {
    padding: 24px 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: #fff;
    border: 1px solid var(--border);
    border-radius: 8px;
    transition: all 0.3s ease;
    box-shadow: var(--shadow-sm);
  }

  .jmk-service-card:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg);
    border-color: var(--teal);
  }

  .jmk-service-icon {
    width: 44px;
    height: 44px;
    background: var(--teal);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    border-radius: 8px;
    flex-shrink: 0;
  }

  .jmk-service-card:hover .jmk-service-icon {
    transform: scale(1.05);
  }

  .jmk-service-label {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--ink);
    line-height: 1.4;
    font-family: 'Inter', sans-serif;
  }

  /* =========================================================
     REACH
  ========================================================= */

  .jmk-reach-wrap {
    display: grid;
    grid-template-columns: 0.85fr 1.15fr;
    gap: 0;
    border: 1px solid var(--border);
    border-radius: 8px;
    overflow: hidden;
    margin-top: 30px;
  }

  .jmk-div-list {
    background: #fff;
  }

  .jmk-div-btn {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    text-align: left;
    background: #fff;
    border: none;
    border-bottom: 1px solid var(--border);
    padding: 14px 20px;
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    font-size: 13.5px;
    color: var(--ink);
    transition: all 0.2s;
  }

  .jmk-div-btn:last-child {
    border-bottom: none;
  }

  .jmk-div-btn:hover {
    background: var(--teal-light);
  }

  .jmk-div-btn.active {
    background: var(--teal);
    color: #fff;
    font-weight: 600;
  }

  .jmk-div-btn .yr {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: var(--gray);
  }

  .jmk-div-btn.active .yr {
    color: rgba(255,255,255,0.8);
  }

  .jmk-div-detail {
    padding: 28px;
    background: var(--teal-light);
  }

  .jmk-div-detail h4 {
    font-family: 'Playfair Display', serif;
    font-weight: 700;
    color: var(--teal-dark);
    font-size: 19px;
    margin-bottom: 4px;
  }

  .jmk-div-detail .since {
    font-family: 'JetBrains Mono', monospace;
    color: var(--teal);
    font-size: 12px;
    display: block;
    margin-bottom: 14px;
    font-weight: 600;
  }

  .jmk-div-detail .div-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin: 14px 0;
  }

  .jmk-div-detail .stat-box {
    background: #fff;
    padding: 10px 14px;
    border-radius: 6px;
    text-align: center;
    border: 1px solid var(--border);
  }

  .jmk-div-detail .stat-box .num {
    font-family: 'JetBrains Mono', monospace;
    font-size: 17px;
    font-weight: 700;
    color: var(--teal-dark);
  }

  .jmk-div-detail .stat-box .label {
    font-size: 9px;
    text-transform: uppercase;
    color: var(--gray);
    letter-spacing: 0.06em;
    font-weight: 600;
    font-family: 'Inter', sans-serif;
  }

  .jmk-div-detail .div-projects {
    margin-top: 14px;
  }

  .jmk-div-detail .div-projects h5 {
    font-size: 12px;
    color: var(--gray);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 600;
    margin-bottom: 6px;
    font-family: 'Inter', sans-serif;
  }

  .jmk-div-detail .div-projects ul {
    list-style: none;
    padding: 0;
  }

  .jmk-div-detail .div-projects ul li {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 3px 0;
    font-size: 13px;
    color: var(--ink);
    font-family: 'Inter', sans-serif;
  }

  .jmk-div-detail .div-projects ul li svg {
    color: var(--teal);
    flex-shrink: 0;
  }

  .jmk-div-detail p {
    font-size: 14px;
    color: var(--ink);
    line-height: 1.7;
    margin-top: 10px;
    font-family: 'Inter', sans-serif;
  }

  /* =========================================================
     PROJECTS
  ========================================================= */

  .jmk-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 30px;
  }

  .jmk-tab {
    background: #fff;
    border: 1px solid var(--border);
    border-bottom: none;
    padding: 10px 18px;
    font-size: 11.5px;
    font-weight: 600;
    cursor: pointer;
    color: var(--gray);
    font-family: 'Inter', sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    transition: all 0.2s;
    border-radius: 6px 6px 0 0;
  }

  .jmk-tab:hover {
    color: var(--teal);
  }

  .jmk-tab.active {
    background: var(--teal);
    color: #fff;
    border-color: var(--teal);
  }

  .jmk-tab-body {
    border: 1px solid var(--border);
    padding: 28px;
    background: var(--teal-light);
    border-radius: 0 6px 6px 6px;
  }

  .jmk-tab-intro {
    font-size: 14px;
    color: var(--gray);
    max-width: 780px;
    margin: 0 0 20px;
    line-height: 1.7;
    font-family: 'Inter', sans-serif;
  }

  .jmk-cards {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 18px;
  }

  .jmk-card {
    background: #fff;
    padding: 22px;
    border-radius: 6px;
    border-left: 4px solid var(--teal);
    box-shadow: var(--shadow-sm);
    transition: all 0.3s ease;
  }

  .jmk-card:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-md);
  }

  .jmk-card h5 {
    font-family: 'Playfair Display', serif;
    font-size: 15px;
    color: var(--teal-dark);
    font-weight: 700;
  }

  .jmk-card .meta {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10.5px;
    color: var(--teal);
    margin: 5px 0 8px;
    display: block;
    font-weight: 600;
  }

  .jmk-card p {
    font-size: 13px;
    color: var(--gray);
    margin: 0;
    line-height: 1.6;
    font-family: 'Inter', sans-serif;
  }

  /* =========================================================
     CLIENTS
  ========================================================= */

  .jmk-clients-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    margin-top: 30px;
  }

  .jmk-client-tile {
    background: #fff;
    padding: 20px 14px;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--teal-dark);
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 64px;
    border: 1px solid var(--border);
    border-radius: 8px;
    transition: all 0.3s;
    font-family: 'Inter', sans-serif;
  }

  .jmk-client-tile:hover {
    border-color: var(--teal);
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }

  /* =========================================================
     LOGIN BUTTON
  ========================================================= */

  .jmk-login-btn {
    background: var(--teal-gradient);
    color: #fff;
    border: none;
    padding: 8px 22px;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 600;
    font-family: 'Inter', sans-serif;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    letter-spacing: 0.04em;
    box-shadow: 0 2px 8px rgba(0, 137, 134, 0.3);
  }

  .jmk-login-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 137, 134, 0.4);
    background: linear-gradient(135deg, #008986 0%, #00a89e 50%, #00c4b8 100%);
  }

  /* =========================================================
     CONTACT SECTION
     LEFT = ONE COLUMN
     RIGHT = SEND MESSAGE
  ========================================================= */

  .jmk-contact-grid {
    display: grid;
    grid-template-columns: 0.9fr 1.1fr;
    gap: 0;
    border: 1px solid var(--border);
    border-radius: 10px;
    overflow: hidden;
    margin-top: 30px;
    box-shadow: var(--shadow-sm);
    background: #fff;
  }

  /* =========================================================
     CONTACT LEFT SIDE
  ========================================================= */

  .jmk-contact-left {
    display: flex;
    flex-direction: column;
    background: #fff;
  }

  .jmk-contact-item {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 22px 24px;
    min-height: 90px;
    border-bottom: 1px solid var(--border);
    transition: background 0.2s ease;
    font-family: 'Inter', sans-serif;
  }

  .jmk-contact-item:last-child {
    border-bottom: none;
  }

  .jmk-contact-item:hover {
    background: var(--teal-light);
  }

  /* =========================================================
     CONTACT ICON
  ========================================================= */

  .jmk-contact-item .ico {
    width: 44px;
    height: 44px;
    min-width: 44px;
    background: var(--teal);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    border-radius: 8px;
  }

  /* =========================================================
     CONTACT TITLE
  ========================================================= */

  .jmk-contact-item b {
    display: block;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.10em;
    color: var(--gray);
    margin-bottom: 5px;
    font-weight: 700;
  }

  /* =========================================================
     CONTACT TEXT
  ========================================================= */

  .jmk-contact-item div a,
  .jmk-contact-item div p {
    color: var(--teal-dark);
    font-size: 13.5px;
    margin: 0;
    text-decoration: none;
    font-weight: 600;
    line-height: 1.55;
  }

  .jmk-contact-item div a:hover {
    color: var(--teal);
  }

  /* =========================================================
     MESSAGE PANEL
  ========================================================= */

  .jmk-contact-panel.jmk-message-panel {
    background: var(--teal-gradient) !important;
    color: #fff;
    padding: 30px 32px;
    display: block;
    min-height: 100%;
    box-sizing: border-box;
  }

  /* =========================================================
     MESSAGE HEADER
  ========================================================= */

  .jmk-message-heading {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
  }

  .jmk-message-icon {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: rgba(255,255,255,0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
  }

  .jmk-message-heading h4 {
    margin: 0;
    font-family: 'Playfair Display', serif;
    font-size: 21px;
    font-weight: 700;
    color: #fff;
  }

  .jmk-message-line {
    width: 55px;
    height: 2px;
    background: rgba(255,255,255,0.8);
    margin-top: 5px;
  }

  /* =========================================================
     MESSAGE FORM
  ========================================================= */

  .jmk-message-form {
    width: 100%;
  }

  .jmk-message-field {
    margin-bottom: 13px;
  }

  .jmk-message-field label {
    display: block;
    margin-bottom: 6px;
    font-family: 'Inter', sans-serif;
    font-size: 11px;
    font-weight: 600;
    color: rgba(255,255,255,0.95);
  }

  .jmk-message-field label span {
    color: #fff;
  }

  .jmk-message-field input,
  .jmk-message-field textarea {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid rgba(255,255,255,0.45);
    border-radius: 5px;
    background: #fff;
    color: #222;
    padding: 10px 12px;
    font-family: 'Inter', sans-serif;
    font-size: 11px;
    outline: none;
    transition: all 0.2s ease;
  }

  .jmk-message-field input {
    height: 37px;
  }

  .jmk-message-field textarea {
    min-height: 78px;
    resize: vertical;
    line-height: 1.5;
  }

  .jmk-message-field input::placeholder,
  .jmk-message-field textarea::placeholder {
    color: #999;
  }

  .jmk-message-field input:focus,
  .jmk-message-field textarea:focus {
    border-color: #fff;
    box-shadow: 0 0 0 2px rgba(255,255,255,0.18);
  }

  /* =========================================================
     CAPTCHA
  ========================================================= */

  .jmk-simple-captcha {
    width: 205px;
    height: 48px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 6px 8px;
    margin: 4px 0 15px;
    border: 1px solid rgba(255,255,255,0.35);
    background: rgba(255,255,255,0.96);
  }

  .jmk-simple-captcha input {
    width: 18px;
    height: 18px;
    margin: 0;
    cursor: pointer;
  }

  .jmk-simple-captcha label {
    font-family: Arial, sans-serif;
    font-size: 9px;
    color: #333;
    white-space: nowrap;
    cursor: pointer;
  }

  .jmk-captcha-logo {
    margin-left: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1;
  }

  .jmk-captcha-logo span {
    font-size: 17px;
    color: #4285f4;
  }

  .jmk-captcha-logo small {
    font-size: 7px;
    color: #555;
  }

  .jmk-captcha-logo em {
    font-size: 5px;
    color: #777;
    font-style: normal;
  }

  /* =========================================================
     SEND BUTTON
  ========================================================= */

  .jmk-send-message-btn {
    width: 100%;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: none;
    border-radius: 5px;
    background: #fff;
    color: var(--teal-dark);
    font-family: 'Inter', sans-serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    cursor: pointer;
    transition: all 0.25s ease;
  }

  .jmk-send-message-btn:hover {
    background: var(--teal-light);
    color: var(--teal-dark);
    transform: translateY(-1px);
    box-shadow: 0 5px 12px rgba(0,0,0,0.15);
  }

  /* =========================================================
     FOOTER
  ========================================================= */

  .jmk-footer {
    background: var(--teal-dark);
    color: rgba(255,255,255,0.85);
    padding: 50px 0 0;
  }

  .jmk-footer-grid {
    display: grid;
    grid-template-columns: 1.4fr 1fr 1fr 1fr;
    gap: 40px;
    padding-bottom: 36px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
  }

  .jmk-footer-col h6 {
    font-size: 12px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #fff;
    margin-bottom: 16px;
    font-weight: 700;
    font-family: 'Inter', sans-serif;
  }

  .jmk-footer-col a,
  .jmk-footer-col p {
    display: block;
    color: rgba(255,255,255,0.75);
    font-size: 13px;
    margin-bottom: 8px;
    text-decoration: none;
    cursor: pointer;
    transition: color 0.2s;
    font-family: 'Inter', sans-serif;
  }

  .jmk-footer-col a:hover {
    color: #fff;
  }

  .jmk-footer-bottom {
    padding: 20px 0;
    font-size: 12px;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    color: rgba(255,255,255,0.5);
    font-family: 'Inter', sans-serif;
  }

  /* =========================================================
     RESPONSIVE
  ========================================================= */

  @media (max-width: 900px) {

    .jmk-nav {
      display: none;
    }

    .jmk-burger {
      display: block;
    }

    .jmk-nav-wrapper {
      position: static;
    }

    .jmk-hero-grid {
      grid-template-columns: 1fr;
      gap: 40px;
    }

    .jmk-content-row {
      grid-template-columns: 1fr;
      gap: 30px;
    }

    .jmk-content-row.reverse {
      direction: ltr;
    }

    .jmk-reach-wrap {
      grid-template-columns: 1fr;
    }

    .jmk-contact-grid {
      grid-template-columns: 1fr;
    }

    .jmk-services-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .jmk-footer-grid {
      grid-template-columns: 1fr 1fr;
      gap: 28px;
    }
  }

  @media (max-width: 860px) {

    .jmk-clients-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .jmk-contact-grid {
      grid-template-columns: 1fr;
    }

    .jmk-contact-panel.jmk-message-panel {
      min-height: auto;
    }
  }

  @media (max-width: 768px) {

    .jmk-container {
      padding: 0 20px;
    }

    .jmk-header-inner {
      padding: 10px 20px;
    }

    .jmk-nav {
      padding: 0 20px;
    }

    .jmk-mobilemenu {
      padding: 8px 20px 14px;
    }

    .jmk-section {
      padding: 56px 0;
    }

    .jmk-hero {
      padding: 56px 0 40px;
    }

    .jmk-div-detail .div-stats {
      grid-template-columns: 1fr 1fr;
    }

    .jmk-stats-grid {
      grid-template-columns: 1fr 1fr;
    }

    .jmk-brand-name {
      font-size: 16px;
    }

    .jmk-logo-img {
      height: 38px;
    }

    .jmk-login-btn {
      padding: 6px 16px;
      font-size: 12px;
    }
  }

  @media (max-width: 560px) {

    .jmk-services-grid {
      grid-template-columns: 1fr;
    }

    .jmk-cards {
      grid-template-columns: 1fr;
    }

    .jmk-div-detail .div-stats {
      grid-template-columns: 1fr;
    }

    .jmk-footer-grid {
      grid-template-columns: 1fr;
    }

    .jmk-contact-panel.jmk-message-panel {
      padding: 25px 20px;
    }

    .jmk-contact-item {
      padding: 20px;
    }

    .jmk-contact-item div a,
    .jmk-contact-item div p {
      font-size: 13px;
    }

    .jmk-simple-captcha {
      max-width: 100%;
    }
  }
    .jmk-contact-content b {
  display: block;
  margin-bottom: 6px;
}

.jmk-contact-content span {
  display: block;
  line-height: 1.5;
}

  @media (max-width: 480px) {

    .jmk-brand-text {
      display: none;
    }

    .jmk-hero {
      padding: 40px 0 30px;
    }

    .jmk-stats-grid {
      grid-template-columns: 1fr;
    }

    .jmk-login-btn span {
      display: none;
    }

    .jmk-login-btn {
      padding: 8px 12px;
    }

    .jmk-message-heading h4 {
      font-size: 19px;
    }
  }
`}</style>

      {/* UTILITY BAR */}
      <div className="jmk-utility">
        <div className="jmk-container">
          <span className="jmk-utility-badge">ISO 9001:2015 &amp; 27001:2022</span>
          <div className="jmk-utility-links">
  {/* <span><Phone size={13} /> <a href="tel:+917378707794">7378707794</a></span> */}
  <span>
    <Mail size={13} /> 
    <a
      href="#"
      onClick={(e) => {
        e.preventDefault();
        window.open(
          `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(COMPANY_EMAIL)}`,
          '_blank'
        );
      }}
    >
      {COMPANY_EMAIL}
    </a>
  </span>
</div>
        </div>
      </div>

      {/* HEADER - STICKY */}

      <header className="jmk-header" id="header">
        <div className="jmk-header-inner">
          <div className="jmk-brand" onClick={() => goTo("top")}>
            <img
              src={IMAGES.logo}
              alt="JMK Infosoft Solutions Ltd"
              className="jmk-logo-img"
            />
            <div className="jmk-brand-text">
              <div className="jmk-brand-name">JMK INFOSOFT SOLUTIONS LIMITED</div>
              <div className="jmk-brand-tag">ITES & E-GOVERNANCE PROJECTS EXECUTION PARTNER</div>
            </div>
          </div>

          {/* Add Login Button Here */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              className="jmk-login-btn"
              onClick={() => window.location.href = '/login'}
            >
              <i className="fas fa-sign-in-alt" style={{ marginRight: '8px' }}></i>
              Login
            </button>
            <button className="jmk-burger" onClick={() => setNavOpen(!navOpen)}>
              {navOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* NAVIGATION - STICKY BELOW HEADER */}
      <div className="jmk-nav-wrapper">
        <nav className="jmk-nav">
          {SECTIONS.map((s) => (
            <button key={s.id} className={activeSection === s.id ? "active" : ""} onClick={() => goTo(s.id)}>
              {s.label}
            </button>
          ))}
        </nav>
        <div className={`jmk-mobilemenu ${navOpen ? "open" : ""}`}>
          {SECTIONS.map((s) => (
            <button key={s.id} onClick={() => goTo(s.id)}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* BREADCRUMB */}
      <div className="jmk-breadcrumb">
        <div className="jmk-container">
          <span>Home</span> <span>/</span> <b>{activeLabel}</b>
        </div>
      </div>

 <NoticeBoard />
      {/* HERO */}
      <section className="jmk-hero" id="top">
        <div className="jmk-container jmk-hero-grid">
          <div className="jmk-hero-left">
            <div className="jmk-hero-badge">
              <Award size={14} /> Established 1998
            </div>
            <h1>
              A Trusted Government<br />
              Field-Execution Partner
            </h1>
            <p className="jmk-hero-sub">
              JMK Infosoft Solutions Limited delivers large-scale, mission-critical Government and
              PSU projects across India, with primary operations spanning all six revenue divisions
              of Maharashtra State.
            </p>
            <div className="jmk-hero-ctas">
              <button className="jmk-btn-primary" onClick={() => goTo("projects")}>
                View Project Record <ArrowRight size={16} />
              </button>
              <button className="jmk-btn-ghost" onClick={() => goTo("contact")}>
                Contact / Enquiry
              </button>
            </div>
          </div>
          <div className="jmk-snapshot">
            <div className="jmk-snapshot-head">Corporate Snapshot</div>
            <div className="jmk-snapshot-row"><span>Foundation</span><span>1998</span></div>
            <div className="jmk-snapshot-row"><span>Incorporated</span><span>08 Jul 2011</span></div>
            <div className="jmk-snapshot-row"><span>On-roll Employees</span><span>500+</span></div>
            <div className="jmk-snapshot-row"><span>Talukas Covered</span><span>358 / 358</span></div>
            <div className="jmk-snapshot-row"><span>Election Cycles Served</span><span>Since 1999</span></div>
          </div>
        </div>
      </section>

      {/* STATS */}
      {/* <div className="jmk-stats-bar">
        <div className="jmk-container">
          <div className="jmk-stats-grid">
            {STATS.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div className="jmk-stat-item" key={i}>
                  <div className="jmk-stat-icon"><Icon size={22} /></div>
                  <div>
                    <div className="jmk-stat-value">{stat.value}</div>
                    <div className="jmk-stat-label">{stat.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div> */}

      <main>
        {/* ABOUT */}
        <section className="jmk-section jmk-band-white" id="about">
          <div className="jmk-container">
            <div className="jmk-sechead">
              <div>
                {/* <div className="jmk-secnum">Section 01</div> */}
                <h2 className="jmk-h2">About the Company</h2>
              </div>
              <div className="jmk-sechead-ref">Profile Ref: JMK / CORP / 2026</div>
            </div>
            <p className="jmk-lede">
              JMK Infosoft Solutions Limited is an ITES services and field-execution partner
              delivering large-scale, mission-critical Government and PSU projects across India —
              largely across Maharashtra State.
            </p>

            <div className="jmk-content-row">
              <div className="jmk-content-image">
                <img src={IMAGES.aboutImage} alt="About JMK Infosoft" />
              </div>
              <div className="jmk-content-text">
                <h3>Corporate Particulars</h3>
                <ul>
                  <li><CheckCircle2 size={16} /> <strong>Foundation:</strong> 1998</li>
                  <li><CheckCircle2 size={16} /> <strong>Incorporation:</strong> 08 July 2011 (Companies Act, 1956)</li>
                  {DIRECTORS.map((d, i) => (
                    <li key={i}><CheckCircle2 size={16} /> <strong>Director:</strong> {d}</li>
                  ))}
                  <li><CheckCircle2 size={16} /> <strong>ISO 9001:2015</strong> — Quality Management Systems</li>
                  <li><CheckCircle2 size={16} /> <strong>ISO 27001:2022</strong> — Information Security Management</li>
                  <li><CheckCircle2 size={16} /> <strong>PAN-Maharashtra</strong> presence across all 358 talukas</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CAPABILITIES */}
        <section className="jmk-section jmk-band-light" id="capabilities">
          <div className="jmk-container">
            <div className="jmk-sechead">
              <div>
                {/* <div className="jmk-secnum">Section 02</div> */}
                <h2 className="jmk-h2">Core Capabilities</h2>
              </div>
              <div className="jmk-sechead-ref">Eight capability statements</div>
            </div>

            <div className="jmk-content-row reverse">
              <div className="jmk-content-image">
                <img src={IMAGES.capabilitiesImage} alt="JMK Capabilities" />
              </div>
              <div className="jmk-content-text">
                <h3>Our Strengths</h3>
                <ul>
                  {CAPABILITIES.map((c, i) => (
                    <li key={i}><CheckCircle2 size={16} /> {c}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section className="jmk-section jmk-band-white" id="services">
          <div className="jmk-container">
            <div className="jmk-sechead">
              <div>
                {/* <div className="jmk-secnum">Section 03</div> */}
                <h2 className="jmk-h2">Service Portfolio</h2>
              </div>
              <div className="jmk-sechead-ref">Twelve service lines</div>
            </div>

            <div className="jmk-content-row">
              <div className="jmk-content-image">
                <img src={IMAGES.servicesImage} alt="JMK Services" />
              </div>
              <div className="jmk-content-text">
                <h3>Our Services</h3>
                <ul>
                  {SERVICES.map((s, i) => (
                    <li key={i}><CheckCircle2 size={16} /> {s.label}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* REACH */}
        <section className="jmk-section jmk-band-light" id="reach">
          <div className="jmk-container">
            <div className="jmk-sechead">
              <div>
                {/* <div className="jmk-secnum">Section 04</div> */}
                <h2 className="jmk-h2">Divisional Presence — Maharashtra State</h2>
              </div>
              <div className="jmk-sechead-ref">6 revenue divisions · 358 talukas</div>
            </div>

            <div className="jmk-content-row reverse">
              <div className="jmk-content-image">
                <img src={IMAGES.reachImage} alt="Maharashtra Map" />
              </div>
              <div className="jmk-content-text">
                <h3>Operational Footprint</h3>
                <ul>
                  {DIVISIONS.map((d, i) => (
                    <li key={i}><CheckCircle2 size={16} /> <strong>{d.name}</strong> — Since {d.since}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="jmk-reach-wrap">
              <div className="jmk-div-list">
                {DIVISIONS.map((d) => (
                  <button
                    key={d.id}
                    className={`jmk-div-btn ${activeDiv === d.id ? "active" : ""}`}
                    onClick={() => setActiveDiv(d.id)}
                  >
                    <span>{d.name.split(" (")[0]}</span>
                    <span className="yr">Since {d.since}</span>
                  </button>
                ))}
              </div>
              <div className="jmk-div-detail">
                <h4>{activeDivision.name}</h4>
                <span className="since">
                  <Calendar size={14} style={{ display: 'inline', marginRight: 6 }} />
                  Active since {activeDivision.since} · {activeDivision.talukas}
                </span>

                <div className="div-stats">
                  <div className="stat-box">
                    <div className="num">{activeDivision.stats.talukas}</div>
                    <div className="label">Talukas</div>
                  </div>
                  <div className="stat-box">
                    <div className="num">{activeDivision.stats.yearsActive}</div>
                    <div className="label">Years Active</div>
                  </div>
                  <div className="stat-box">
                    <div className="num">{activeDivision.stats.recordsProcessed}</div>
                    <div className="label">Records Processed</div>
                  </div>
                </div>

                <p>{activeDivision.detail}</p>

                <div className="div-projects">
                  <h5><Briefcase size={14} style={{ display: 'inline', marginRight: 6 }} /> Key Projects</h5>
                  <ul>
                    {activeDivision.projects.map((project, idx) => (
                      <li key={idx}>
                        <CheckCircle2 size={14} />
                        {project}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PROJECTS */}
        <section className="jmk-section jmk-band-white" id="projects">
          <div className="jmk-container">
            <div className="jmk-sechead">
              <div>
                {/* <div className="jmk-secnum">Section 05</div> */}
                <h2 className="jmk-h2">Project Record, By Client Category</h2>
              </div>
              <div className="jmk-sechead-ref">25-year execution history</div>
            </div>

            <div className="jmk-content-row">
              <div className="jmk-content-image">
                <img src={IMAGES.projectsImage} alt="Projects Showcase" />
              </div>
              <div className="jmk-content-text">
                <h3>25 Years of Excellence</h3>
                <ul>
                  <li><CheckCircle2 size={16} /> <strong>BOOT & Citizen Centres</strong> — 5+ major contracts</li>
                  <li><CheckCircle2 size={16} /> <strong>IT Staffing</strong> — 400+ resources statewide</li>
                  <li><CheckCircle2 size={16} /> <strong>Election Projects</strong> — Since 1999</li>
                  <li><CheckCircle2 size={16} /> <strong>Central Govt. & PSU</strong> — BEL, ECIL, ITI, WIPRO</li>
                  <li><CheckCircle2 size={16} /> <strong>Software & Web</strong> — 20+ custom solutions</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CLIENTS */}
        <section className="jmk-section jmk-band-light" id="clients">
          <div className="jmk-container">
            <div className="jmk-sechead">
              <div>
                {/* <div className="jmk-secnum">Section 06</div> */}
                <h2 className="jmk-h2">Clients &amp; Empanelments</h2>
              </div>
              <div className="jmk-sechead-ref">12 organizations</div>
            </div>

            <div className="jmk-content-row reverse">
              <div className="jmk-content-image">
                <img src={IMAGES.clientsImage} alt="Client Logos" />
              </div>
              <div className="jmk-content-text">
                <h3>Trusted By</h3>
                <ul>
                  {CLIENTS.map((c, i) => (
                    <li key={i}><CheckCircle2 size={16} /> {c}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ========== CAREERS SECTION ========== */}
        <section className="jmk-section jmk-band-white" id="careers">
          <div className="jmk-container">
            <ViewRequirement />
          </div>
        </section>
      </main>

{/* CONTACT */}
<section className="jmk-section jmk-band-white" id="contact" style={{ borderBottom: "none", paddingBottom: "56px" }}>
  <div className="jmk-container">
    <div className="jmk-sechead">
      <div>
        <h2 className="jmk-h2">Contact / Enquiry</h2>
      </div>
      <div className="jmk-sechead-ref">Head Office — Chhatrapati Sambhajinagar</div>
    </div>

    <div className="jmk-contact-grid">

      {/* =====================================================
          LEFT SIDE - SINGLE COLUMN CONTACT DETAILS
      ===================================================== */}
      <div className="jmk-contact-left">

        {/* HEAD OFFICE */}
        <div
          className="jmk-contact-item"
          style={{ cursor: 'pointer' }}
          onClick={() =>
            window.open(
              'https://maps.app.goo.gl/4soLH4iKJG8zN5jz6',
              '_blank'
            )
          }
        >
          <div className="ico">
            <MapPin size={19} />
          </div>
          <div className="jmk-contact-content">
            <b>HEAD OFFICE</b>
            <p>
              Plot No. T-22, STPI Area,
              <br />
              Chikalthana MIDC,
              <br />
              Chhatrapati Sambhajinagar – 431005
            </p>
          </div>
        </div>

        {/* PHONE */}
        <div className="jmk-contact-item">
          <div className="ico">
            <Phone size={19} />
          </div>
          <div className="jmk-contact-content">
            <b>PHONE</b>
            <a href="tel:+917378707794">7378707794</a>
          </div>
        </div>

<div className="jmk-contact-item">
  <div className="ico">
    <Phone size={19} />
  </div>

  <div className="jmk-contact-content">
    <b>Office Timing</b>
    <span>Monday to Saturday</span>
    <span>10:00 AM to 6:30 PM</span>
  </div>
</div>

        {/* EMAIL - येथे Gmail उघडेल */}
        <div className="jmk-contact-item">
          <div className="ico">
            <Mail size={19} />
          </div>
          <div className="jmk-contact-content">
            <b>EMAIL</b>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(COMPANY_EMAIL)}`,
                  '_blank'
                );
              }}
            >
              {COMPANY_EMAIL}
            </a>
          </div>
        </div>

        {/* WEBSITE */}
        <div className="jmk-contact-item">
          <div className="ico">
            <Globe size={19} />
          </div>
          <div className="jmk-contact-content">
            <b>WEBSITE</b>
            <a
              href="https://www.jmkinfosoft.com"
              target="_blank"
              rel="noreferrer"
            >
              www.jmkinfosoft.com
            </a>
          </div>
        </div>

      </div>

      {/* =====================================================
          RIGHT SIDE - SEND MESSAGE (Gmail उघडेल)
      ===================================================== */}
      <div className="jmk-contact-panel jmk-message-panel">

        <div className="jmk-message-heading">
          <div className="jmk-message-icon">
            <Mail size={28} />
          </div>
          <div>
            <h4>SEND US A MESSAGE</h4>
            <div className="jmk-message-line"></div>
          </div>
        </div>

        <form
          className="jmk-message-form"
          onSubmit={(e) => {
            e.preventDefault();

            const form = e.currentTarget;
            const fullName = form.fullName.value.trim();
            const email = form.email.value.trim();
            const mobile = form.mobile.value.trim();
            const message = form.message.value.trim();

            // Validation
            if (!fullName || !email || !mobile || !message) {
              alert("कृपया सर्व आवश्यक फील्ड भरा.");
              return;
            }

            // Email subject आणि body तयार करा
            const subject = `Inquiry in JMK Infosoft - ${fullName}`;
            const body = `Name: ${fullName}
Email: ${email}
Mobile: ${mobile}
Message: ${message}`;

            // Gmail ला pre-filled data सह उघडा
            const gmailLink =
              `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(COMPANY_EMAIL)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
            window.open(gmailLink, '_blank');

            // फॉर्म रीसेट करा
            form.reset();
            alert("✅ तुमचा संदेश तयार झाला आहे. कृपया Gmail मध्ये Send बटण दाबा.");
          }}
        >

          {/* FULL NAME */}
          <div className="jmk-message-field">
            <label>Full Name <span>*</span></label>
            <input
              type="text"
              name="fullName"
              placeholder="Enter your full name"
              required
            />
          </div>

          {/* EMAIL */}
          <div className="jmk-message-field">
            <label>Email Address <span>*</span></label>
            <input
              type="email"
              name="email"
              placeholder="Enter your email address"
              required
            />
          </div>

          {/* MOBILE */}
          <div className="jmk-message-field">
            <label>Mobile Number <span>*</span></label>
            <input
              type="tel"
              name="mobile"
              placeholder="Enter your mobile number"
              pattern="[6-9][0-9]{9}"
              maxLength="10"
              required
            />
          </div>

          {/* MESSAGE */}
          <div className="jmk-message-field">
            <label>How can we help you? <span>*</span></label>
            <textarea
              name="message"
              rows="5"
              placeholder="Write your message here..."
              required
            ></textarea>
          </div>

          {/* CAPTCHA */}
          <div className="jmk-simple-captcha">
            <input
              type="checkbox"
              id="contactRobot"
              required
            />
            <label htmlFor="contactRobot">I'm not a robot</label>
            <div className="jmk-captcha-logo">
              <span>↻</span>
              <small>reCAPTCHA</small>
              <em>Privacy - Terms</em>
            </div>
          </div>

          {/* SEND BUTTON */}
          <button
            type="submit"
            className="jmk-send-message-btn"
          >
            <Mail size={16} />
            SEND MESSAGE
          </button>

        </form>

      </div>

    </div>
  </div>
</section>

      {/* FOOTER */}
      <footer className="jmk-footer">
        <div className="jmk-container">
          <div className="jmk-footer-grid">
            <div className="jmk-footer-col">
              <h6>JMK Infosoft Solutions Ltd.</h6>
              <p>ITES &amp; E-Governance Projects Execution Partner. An ISO 9001:2015 &amp; 27001:2022 Organization.</p>
            </div>
            <div className="jmk-footer-col">
              <h6>Quick Links</h6>
              {SECTIONS.filter((s) => s.id !== "top").map((s) => (
                <a key={s.id} onClick={() => goTo(s.id)}>{s.label}</a>
              ))}
            </div>
            <div className="jmk-footer-col">
              <h6>Certifications</h6>
              <p>ISO 9001:2015</p>
              <p>ISO 27001:2022</p>
            </div>
            <div className="jmk-footer-col">
              <h6>Contact</h6>
              <a href="tel:+917378707794">7378707794</a>
              <a href={`mailto:${COMPANY_EMAIL}`}>{COMPANY_EMAIL}</a>
              <p>Aurangabad, Maharashtra</p>
            </div>
          </div>
          <div className="jmk-footer-bottom">
            <span>© {new Date().getFullYear()} JMK Infosoft Solutions Limited. All rights reserved.</span>
            <span>Incorporated 08 July 2011 under the Companies Act, 1956</span>
          </div>
        </div>
      </footer>
    </div>
  );
} 